import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, CheckCircle2 } from "lucide-react";
import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/primitives";
import { CTABand } from "../components/CTABand";
import { IMAGES } from "../lib/site";

const AIMS = [
    "Encourage practical sustainability",
    "Help hotels measure their environmental impact",
    "Identify opportunities for operational improvement",
    "Connect sustainability with business performance",
    "Encourage employee participation",
    "Support local communities",
    "Promote responsible tourism",
    "Share hospitality sustainability knowledge",
    "Build collaboration between hotels, researchers, technology companies, communities and tourism organizations",
];

const PATHWAY = ["Hawaiʻi", "United States", "Africa", "Global Hospitality"];

const About = () => (
    <main>
        <SEO
            title="About GGH | Global Green Hotel"
            description="Global Green Hotel was created from the belief that hospitality can become a stronger force for environmental responsibility, community development and sustainable business."
        />
        <PageHero
            testid="about-hero"
            eyebrow="About GGH"
            titleLines={["A hospitality initiative", "built for long-term growth"]}
            description="Global Green Hotel is an emerging hospitality sustainability initiative — ambitious, honest, and focused on practical progress that hotels can measure."
        />

        {/* Our Story */}
        <section data-testid="about-story" className="py-24 sm:py-32">
            <div className="mx-auto grid max-w-[1440px] items-center gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20 lg:px-12">
                <div>
                    <Reveal>
                        <Eyebrow>Our Story</Eyebrow>
                        <h2 className="font-display mt-4 text-3xl font-medium tracking-tight text-[#0A1711] sm:text-4xl">
                            Born from a simple belief
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-7 text-base leading-relaxed text-[#4A5D54] md:text-lg">
                            GGH was created from the belief that hospitality can become a
                            stronger force for environmental responsibility, community
                            development and sustainable business.
                        </p>
                        <p className="mt-5 text-base leading-relaxed text-[#4A5D54] md:text-lg">
                            The initiative begins with practical hospitality challenges and aims
                            to develop solutions that can eventually be adapted across different
                            destinations and hospitality markets.
                        </p>
                    </Reveal>
                </div>
                <Reveal delay={0.2} className="relative">
                    <div className="overflow-hidden rounded-2xl border border-[#E8E6E1] shadow-[0_30px_80px_rgba(10,23,17,0.14)]">
                        <img
                            src={IMAGES.chef}
                            alt="A chef carefully plating food in a professional hotel kitchen"
                            className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-[1.03]"
                            loading="lazy"
                        />
                    </div>
                    <div aria-hidden="true" className="absolute -bottom-5 -right-5 -z-[1] h-full w-full rounded-2xl border border-[#C2A160]/40" />
                </Reveal>
            </div>
        </section>

        {/* Founder */}
        <section data-testid="about-founder" className="py-24 sm:py-32">
            <div className="mx-auto grid max-w-[1440px] items-center gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-20 lg:px-12">
                <Reveal className="relative lg:col-span-4">
                    <div className="relative mx-auto max-w-sm overflow-hidden rounded-2xl border border-[#E8E6E1] shadow-[0_30px_80px_rgba(10,23,17,0.14)]">
                        <img
                            src="/assets/oliver-ouma.webp"
                            alt="Portrait of Oliver Ouma, founder of Global Green Hotel"
                            className="aspect-square w-full object-cover"
                            loading="lazy"
                        />
                    </div>
                    <div aria-hidden="true" className="absolute -bottom-5 -right-5 -z-[1] hidden h-full w-full rounded-2xl border border-[#C2A160]/40 lg:block" />
                </Reveal>
                <div className="lg:col-span-8">
                    <Reveal>
                        <Eyebrow>Our Founder</Eyebrow>
                        <h2 className="font-display mt-4 text-3xl font-medium tracking-tight text-[#0A1711] sm:text-4xl">
                            The person behind the initiative
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-7 text-lg font-bold text-[#0A1711]" data-testid="founder-name">
                            Oliver Ouma
                        </p>
                        <p className="mt-1 text-xs font-extrabold uppercase tracking-[0.25em] text-[#A4803F]">
                            Founder, Global Green Hotel · Sustainable Tourism & Hospitality Management Professional
                        </p>
                        <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#4A5D54] md:text-lg">
                            Oliver Ouma is a professional in sustainable tourism and hospitality
                            management, and the founder of Global Green Hotel. He created GGH from
                            the conviction that hotels can protect the environment while becoming
                            stronger, more efficient businesses.
                        </p>
                        <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#4A5D54] md:text-lg">
                            Based in Hawaiʻi, he is developing GGH as a practical, evidence-led
                            initiative that connects hotels, researchers, communities and partners
                            — beginning with food waste and growing toward a broader framework for
                            responsible hospitality.
                        </p>
                    </Reveal>
                    <Reveal delay={0.28}>
                        <blockquote className="mt-9 max-w-2xl border-l-2 border-[#C2A160] pl-6">
                            <p className="font-display text-xl italic leading-relaxed text-[#1A3B2B]">
                                “Sustainability should protect the planet, strengthen people and
                                communities, and make good business sense.”
                            </p>
                            <footer className="mt-3 text-xs font-extrabold uppercase tracking-[0.25em] text-[#A4803F]">
                                Oliver Ouma — Founder, GGH
                            </footer>
                        </blockquote>
                    </Reveal>
                </div>
            </div>
        </section>

        {/* Philosophy */}
        <section data-testid="about-philosophy" className="grain relative overflow-hidden bg-[#04150D] py-24 sm:py-32">
            <div className="relative z-[2] mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
                <Reveal>
                    <Eyebrow dark>Our Philosophy</Eyebrow>
                </Reveal>
                <Reveal delay={0.15}>
                    <p className="font-display mt-8 max-w-4xl text-3xl font-medium leading-[1.2] tracking-tight text-[#FDFBF7] sm:text-5xl">
                        Sustainability should create{" "}
                        <em className="italic text-[#D9B96A]">environmental value</em> and{" "}
                        <em className="italic text-[#D9B96A]">business value.</em>
                    </p>
                </Reveal>
            </div>
        </section>

        {/* Our Aim */}
        <section data-testid="about-aim" className="bg-[#F2EFE7] py-24 sm:py-32">
            <div className="mx-auto grid max-w-[1440px] gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-16 lg:px-12">
                <div className="lg:col-span-5">
                    <div className="lg:sticky lg:top-32">
                        <Reveal>
                            <Eyebrow>Our Aim</Eyebrow>
                            <h2 className="font-display mt-4 text-3xl font-medium tracking-tight text-[#0A1711] sm:text-4xl">
                                From good intentions to measurable action
                            </h2>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <p className="mt-7 text-base leading-relaxed text-[#4A5D54] md:text-lg">
                                GGH aims to accelerate sustainable hospitality by helping hotels
                                move from good intentions to measurable action. GGH seeks to:
                            </p>
                        </Reveal>
                    </div>
                </div>
                <ul className="grid content-start gap-3 lg:col-span-7" data-testid="aims-list">
                    {AIMS.map((aim, i) => (
                        <Reveal key={aim} delay={0.05 * i} y={18}>
                            <li className="card-lift flex items-center gap-4 rounded-2xl border border-[#E8E6E1] bg-white p-5">
                                <CheckCircle2 size={20} className="shrink-0 text-[#1A3B2B]" aria-hidden="true" />
                                <span className="text-sm font-semibold leading-snug text-[#0A1711]">{aim}</span>
                            </li>
                        </Reveal>
                    ))}
                </ul>
            </div>
        </section>

        {/* Long-term direction */}
        <section data-testid="about-direction" className="py-24 sm:py-32">
            <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
                <Reveal>
                    <Eyebrow>Our Long-Term Direction</Eyebrow>
                    <h2 className="font-display mt-4 max-w-3xl text-3xl font-medium tracking-tight text-[#0A1711] sm:text-4xl">
                        Growing from Hawaiʻi into a broader international platform
                    </h2>
                </Reveal>
                <Reveal delay={0.15}>
                    <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#4A5D54] md:text-lg">
                        GGH aims to grow from a Hawaiʻi-based initiative into a broader
                        international hospitality sustainability platform. This is our direction
                        of travel — not a claim of operations we already run.
                    </p>
                </Reveal>
                <ol className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="growth-pathway">
                    {PATHWAY.map((place, i) => (
                        <Reveal key={place} delay={0.1 * i} y={22}>
                            <li className="card-lift relative h-full rounded-2xl border border-[#E8E6E1] bg-white p-6">
                                <span className="font-display text-4xl font-medium text-[#1A3B2B]/15">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <p className="font-display mt-3 text-xl font-medium text-[#0A1711]">{place}</p>
                                {i === 0 && (
                                    <span className="mt-3 inline-block rounded-full bg-[#1A3B2B]/5 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#1A3B2B]">
                                        Where we begin
                                    </span>
                                )}
                                {i < PATHWAY.length - 1 && (
                                    <ArrowRight
                                        size={18}
                                        aria-hidden="true"
                                        className="absolute -right-3 top-1/2 hidden -translate-y-1/2 text-[#C2A160] lg:block"
                                    />
                                )}
                            </li>
                        </Reveal>
                    ))}
                </ol>
            </div>
        </section>

        <CTABand
            testid="cta-band-about"
            heading="Grow With GGH"
            text="We welcome conversations with hotels, researchers, tourism organizations and partners who share this direction."
            primary={{ to: "/get-involved", label: "Get Involved" }}
            secondary={{ to: "/contact", label: "Contact GGH" }}
        />
    </main>
);

export default About;
